if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.sectionId = this.dataset.sectionId;
        this.colorOptionIndex = Number(this.dataset.colorOptionPosition || 0) - 1;
        this.selectedColor = this.dataset.selectedColor || '';
        this.variantFeaturedMediaId = this.elements.viewer?.querySelector('.is-active[data-media-id]')?.dataset.mediaId;

        if (
          this.colorOptionIndex >= 0 &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }

        requestAnimationFrame(() => {
          this.applyColorFilter(this.selectedColor, this.variantFeaturedMediaId);
        });

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onVariantChange({ data }) {
        if (data?.sectionId && this.sectionId && data.sectionId !== this.sectionId) return;

        const selectedColor = data?.variant?.options?.[this.colorOptionIndex] || this.selectedColor;
        const featuredMediaId = data?.variant?.featured_media?.id
          ? `${this.sectionId}-${data.variant.featured_media.id}`
          : this.variantFeaturedMediaId;

        this.selectedColor = selectedColor;
        this.variantFeaturedMediaId = featuredMediaId;
        this.dataset.selectedColor = selectedColor;

        requestAnimationFrame(() => {
          this.applyColorFilter(selectedColor, featuredMediaId);
        });
      }

      normalizeColor(value) {
        return (value || '').trim().toLocaleLowerCase();
      }

      shouldShowItem(item, selectedColor, featuredMediaId, groupingEnabled) {
        if (!groupingEnabled) return true;
        if (item.dataset.galleryShared === 'true') return true;

        const itemColor = this.normalizeColor(item.dataset.galleryColor);
        if (itemColor) return itemColor === selectedColor;

        const itemMediaId = item.dataset.mediaId || item.dataset.target;
        if (item.dataset.galleryVariant === 'true') return itemMediaId === featuredMediaId;

        return false;
      }

      updateItemVisibility(item, visible) {
        item.classList.toggle('os-color-gallery-hidden', !visible);
        item.setAttribute('aria-hidden', visible ? 'false' : 'true');

        const interactiveElement = item.querySelector('button, a');
        if (!interactiveElement) return;
        if (visible) {
          interactiveElement.removeAttribute('tabindex');
        } else {
          interactiveElement.setAttribute('tabindex', '-1');
          interactiveElement.removeAttribute('aria-current');
        }
      }

      moveSharedItemsLast(items) {
        const parent = items[0]?.parentElement;
        if (!parent) return;

        items
          .filter((item) => item.dataset.galleryShared === 'true')
          .forEach((item) => parent.append(item));
      }

      filterModal(selectedColor, featuredMediaId, groupingEnabled) {
        const modalContent = this.closest('product-info')?.querySelector(
          'product-modal .product-media-modal__content'
        );
        if (!modalContent) return;

        const modalItems = Array.from(modalContent.children).filter((item) => item.hasAttribute('data-media-id'));
        modalItems.forEach((item) => {
          const visible = this.shouldShowItem(item, selectedColor, featuredMediaId, groupingEnabled);
          item.classList.toggle('os-color-gallery-hidden', !visible);
          item.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });
        if (groupingEnabled) this.moveSharedItemsLast(modalItems);
      }

      applyColorFilter(selectedColorValue, featuredMediaId) {
        if (this.colorOptionIndex < 0 || !this.elements.viewer) return;

        const selectedColor = this.normalizeColor(selectedColorValue);
        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const thumbnailItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]'))
          : [];
        const allGalleryItems = [...viewerItems, ...thumbnailItems];
        const groupingEnabled =
          selectedColor !== '' &&
          allGalleryItems.some(
            (item) => this.normalizeColor(item.dataset.galleryColor) === selectedColor
          );

        viewerItems.forEach((item) => {
          this.updateItemVisibility(
            item,
            this.shouldShowItem(item, selectedColor, featuredMediaId, groupingEnabled)
          );
        });
        thumbnailItems.forEach((item) => {
          this.updateItemVisibility(
            item,
            this.shouldShowItem(item, selectedColor, featuredMediaId, groupingEnabled)
          );
        });

        if (groupingEnabled) {
          this.moveSharedItemsLast(viewerItems);
          this.moveSharedItemsLast(thumbnailItems);
        }

        const visibleThumbnails = thumbnailItems.filter(
          (item) => !item.classList.contains('os-color-gallery-hidden')
        );
        visibleThumbnails.forEach((item, index) => {
          item.dataset.mediaPosition = String(index + 1);
        });

        this.filterModal(selectedColor, featuredMediaId, groupingEnabled);

        const visibleViewerItems = viewerItems.filter(
          (item) => !item.classList.contains('os-color-gallery-hidden')
        );
        const requestedMedia = featuredMediaId
          ? visibleViewerItems.find((item) => item.dataset.mediaId === featuredMediaId)
          : null;
        const activeMedia = visibleViewerItems.find((item) => item.classList.contains('is-active'));
        const mediaToActivate = requestedMedia || activeMedia || visibleViewerItems[0];

        if (mediaToActivate && !mediaToActivate.classList.contains('is-active')) {
          this.setActiveMedia(mediaToActivate.dataset.mediaId, false);
        }

        requestAnimationFrame(() => {
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
